import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  alpha,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  ArrowForward,
  PointOfSale,
  Inventory,
  AccountBalanceWallet,
  PeopleAlt,
  QueryStats,
  AdminPanelSettings,
  CheckCircleOutlineOutlined,
  CloudDone,
  Security,
  Speed,
  TrendingUp,
} from "@mui/icons-material";
import { useAppSelector } from "../redux/store";
import SetupCompanyModal from "../components/company/SetupCompanyModal";

const SoftwareHomePage: React.FC = () => {
  const theme = useTheme();
  const { loadingCompanySetup: loadingCompanySetup, errorSetupCompany } =
    useAppSelector((state) => state.company);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState<boolean>(false);

  // Grouped features based on your actual ERP modules
  const coreModules = [
    {
      icon: <PointOfSale fontSize="large" sx={{ color: "#00b0ff" }} />,
      color: "#00b0ff",
      title: "Omnichannel Sales & POS",
      description:
        "Accelerate checkout and manage complex B2B/B2C sales pipelines seamlessly.",
      features: [
        "Intuitive Point of Sale (POS)",
        "Quotations & Invoicing",
        "Customer Orders & Shifts",
        "Credit Notes & Receipts",
      ],
    },
    {
      icon: <Inventory fontSize="large" sx={{ color: "#ff9800" }} />,
      color: "#ff9800",
      title: "Advanced Inventory",
      description:
        "Prevent stockouts and track product movements with absolute precision across warehouses.",
      features: [
        "Multi-level Stock Tracking",
        "Brands & Categories",
        "Package & Quantity Units",
        "Purchase Orders & Suppliers",
      ],
    },
    {
      icon: <AccountBalanceWallet fontSize="large" sx={{ color: "#00e676" }} />,
      color: "#00e676",
      title: "Financial Control",
      description:
        "Maintain a flawless ledger, track expenses, and manage corporate debts effortlessly.",
      features: [
        "Chart of Accounts",
        "General Ledger Entries",
        "Expense Tracking",
        "Debt & Payment Management",
      ],
    },
    {
      icon: <PeopleAlt fontSize="large" sx={{ color: "#b388ff" }} />,
      color: "#b388ff",
      title: "CRM & Communications",
      description:
        "Build lasting relationships with automated outreach and dedicated customer portals.",
      features: [
        "Customer Database",
        "Client Portal Access",
        "SMS Templates & Outbox",
        "System Announcements",
      ],
    },
    {
      icon: <QueryStats fontSize="large" sx={{ color: "#ff4081" }} />,
      color: "#ff4081",
      title: "Business Intelligence",
      description:
        "Transform raw data into actionable insights with enterprise-grade visual analytics.",
      features: [
        "Sales & Profit Trends",
        "Quote Conversion Funnels",
        "Top Performing Branches",
        "Expense Breakdowns",
      ],
    },
    {
      icon: <AdminPanelSettings fontSize="large" sx={{ color: "#4cceac" }} />,
      color: "#4cceac",
      title: "Enterprise Administration",
      description:
        "Total control over your organization's security, compliance, and structure.",
      features: [
        "Multi-Branch Architecture",
        "Granular Roles & Permissions",
        "Custom Tax Codes & Currencies",
        "Immutable Audit Logs",
      ],
    },
  ];

  const benefits = [
    {
      icon: <CloudDone fontSize="large" sx={{ color: "#ff9800" }} />,
      title: "Cloud Native",
      desc: "Access your business from anywhere, on any device, with 99.9% guaranteed uptime.",
    },
    {
      icon: <Speed color="secondary" fontSize="large" />,
      title: "Lightning Fast",
      desc: "Optimized architecture ensures rapid POS checkouts and instant report generation.",
    },
    {
      icon: <Security color="error" fontSize="large" />,
      title: "Bank-Grade Security",
      desc: "Role-based access control and comprehensive audit trails protect your data.",
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        overflowX: "hidden",
      }}
    >
      {/* 1. HERO SECTION */}
      <Box
        sx={{
          pt: { xs: 10, md: 15 },
          pb: { xs: 8, md: 12 },
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${theme.palette.background.default} 100%)`,
        }}
      >
        <Container maxWidth="lg" sx={{ textAlign: "center" }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 800,
              mb: 1,
              fontSize: { xs: "2.5rem", md: "4rem" },
              letterSpacing: "-0.02em",
            }}
          >
            The modern tool for <br />
            <Box component="span" sx={{ color: theme.palette.secondary.main }}>
              Business & Trade
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              maxWidth: "800px",
              mx: "auto",
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            Fiji ERP unifies your Point of Sale, Inventory, Financials, and
            Customer Relations into one lightning-fast platform. Scale from a
            single store to a nationwide enterprise seamlessly.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => setIsSetupModalOpen(true)}
              sx={{ px: 4, py: 1.5, fontSize: "1.1rem", borderRadius: "30px" }}
            >
              Start Your Free Setup
            </Button>
          </Box>
        </Container>
      </Box>

      {/* 2. TRUST / BENEFITS STRIP */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Grid container spacing={4} sx={{ justifyContent: "center" }}>
          {benefits.map((benefit, idx) => (
            <Grid
              size={{ xs: 12, md: 4 }}
              key={idx}
              sx={{ textAlign: "center" }}
            >
              <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                {benefit.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {benefit.desc}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Divider />

      {/* 3. CORE MODULES GRID */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="subtitle1"
              color="secondary"
              sx={{
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: 2,
              }}
            >
              Comprehensive Feature Set
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1 }}>
              Everything You Need to Succeed
            </Typography>
            <Typography
              sx={{ color: "text.secondary", maxWidth: "600px", mx: "auto" }}
            >
              Stop juggling multiple software subscriptions. Fiji ERP provides
              native modules for every department in your company.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {coreModules.map((mod, index) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: `0 10px 30px ${alpha(mod.color, 0.15)}`,
                      borderColor: alpha(mod.color, 0.3),
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        bgcolor: alpha(mod.color, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                      }}
                    >
                      {mod.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                      {mod.title}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", mb: 3 }}>
                      {mod.description}
                    </Typography>

                    <List dense disablePadding>
                      {mod.features.map((feat, fIdx) => (
                        <ListItem key={fIdx} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleOutlineOutlined
                              sx={{ color: mod.color, fontSize: 20 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feat}
                            slotProps={{
                              primary: {
                                sx: { variant: "body2", fontWeight: 500 },
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 4. DEEP DIVE HIGHLIGHT */}
      <Box sx={{ py: 12, bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle1"
                color="secondary"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Scale Without Limits
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", mt: 1, mb: 3 }}
              >
                Unprecedented Visibility with Built-in BI
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ color: "text.secondary", fontSize: "1.1rem", mb: 3 }}
              >
                Make data-driven decisions confidently. Our native Business
                Intelligence engine automatically analyzes your Sales,
                Procurement, and Financial data to generate beautiful,
                interactive trend reports.
              </Typography>
              <List>
                <ListItem disableGutters>
                  <ListItemIcon>
                    <TrendingUp color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Track Branch Performance in Real-time" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon>
                    <TrendingUp color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Analyze Quote-to-Invoice Conversion Funnels" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon>
                    <TrendingUp color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Identify Top Selling Brands and Reps automatically" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 3,
                  bgcolor: "background.paper",
                  borderRadius: 4,
                  boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {/* Abstract mockup of a dashboard */}
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      flex: 1,
                      height: 100,
                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                      borderRadius: 2,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      height: 100,
                      bgcolor: alpha(theme.palette.info.main, 0.2),
                      borderRadius: 2,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: 200,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "flex-end",
                    p: 2,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: "20%",
                      height: "40%",
                      bgcolor: theme.palette.primary.main,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                  <Box
                    sx={{
                      width: "20%",
                      height: "70%",
                      bgcolor: theme.palette.primary.main,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                  <Box
                    sx={{
                      width: "20%",
                      height: "50%",
                      bgcolor: theme.palette.primary.main,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                  <Box
                    sx={{
                      width: "20%",
                      height: "90%",
                      bgcolor: theme.palette.secondary.main,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                  <Box
                    sx={{
                      width: "20%",
                      height: "100%",
                      bgcolor: theme.palette.secondary.main,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 5. BOTTOM CTA */}
      <Box
        sx={{
          py: 10,
          bgcolor: theme.palette.primary.dark,
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: "bold", mb: 1 }}>
            Ready to upgrade your operations?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, mb: 5 }}>
            Join forward-thinking businesses already scaling with Fiji ERP.
            Setup takes less than 5 minutes.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => setIsSetupModalOpen(true)}
            sx={{
              px: 5,
              py: 2,
              fontSize: "1.2rem",
              borderRadius: "30px",
              boxShadow: `0 8px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
            }}
          >
            Create Your Company Profile
          </Button>
        </Container>
      </Box>

      {/* Modals */}
      <SetupCompanyModal
        open={isSetupModalOpen}
        loading={loadingCompanySetup}
        onClose={() => setIsSetupModalOpen(false)}
        error={errorSetupCompany}
      />
    </Box>
  );
};

export default SoftwareHomePage;
